import { useEffect, useRef, useState } from "react";

import { Editor } from "@monaco-editor/react";
import { AlertTriangle } from "lucide-react";

import { cn } from "@/lib/utils";

/**
 * The editor of last resort for an enrichment proposal.
 *
 * **Who needs it.** `movie`, `person` and `show_genre` are gated enrichment
 * flows with no structured enrichment form — their edit pages edit the entity,
 * not a pending proposal — so this is the *only* way a curator can change
 * their proposal before approving it. It is also the drift fallback for `artist`/`venue`: a
 * proposal Tina wrote but IMS's Zod mirror rejects is a mirror-drift bug, and
 * stranding the queue over one is worse than showing raw JSON.
 *
 * **The one hard rule.** Nothing the resolve route cannot safely write must
 * reach it. Two shapes qualify, and only the first is obvious:
 *
 * - Invalid JSON — a `{kind: "enrichment.replace"}` carrying a broken DTO 400s
 *   deep inside the backend's validation with a message no curator can act on.
 * - `{}` — which parses, and is the *dangerous* one. `PUT /{entity}/enrich` is
 *   a **full replace** onto a Firestore document with no version history, and
 *   Tina's `ReplaceEnrichment.enrichment` is an unvalidated `dict[str, Any]`.
 *   So an empty object is not a no-op: it erases the entity's enrichment and
 *   marks the job Enriched, with nothing anywhere to restore it from.
 *
 * Both run on every keystroke, and `onValidityChange(false)` is what disables
 * the submit — rather than the submit handler trying to parse and bail.
 *
 * Deliberately not the existing `@/components/fields/json-editor` (a modal with
 * its own Save button and an `any`-typed ref): the proposal editor has to sit
 * *inline* under the read-only review cards, and its "save" is the parent's
 * resolve, not a dialog dismissal.
 */
export function JsonEnrichmentEditor({
  value,
  onChange,
  onValidityChange,
  height = 420,
  className,
}: {
  value: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
  onValidityChange: (isValid: boolean) => void;
  height?: number;
  className?: string;
}) {
  const [text, setText] = useState(() => stringify(value));
  const [error, setError] = useState<string | null>(null);

  // Re-seed only when the *parent* swaps the proposal (entity change, a retry
  // landing a new one). Keyed on the serialised form so the curator's own
  // keystrokes — which flow back up through onChange — do not reset the buffer
  // mid-edit.
  const lastExternal = useRef(stringify(value));
  useEffect(() => {
    const next = stringify(value);
    if (next === lastExternal.current) return;
    lastExternal.current = next;
    setText(next);
    // Re-run the same checks rather than assuming an externally supplied
    // proposal is submittable: a park whose payload did not survive the jsonb
    // round-trip arrives here as `{}`, and reporting that as valid is how the
    // submit gets enabled over a proposal there is nothing to write.
    const seeded = validate(next);
    setError(seeded.ok ? null : seeded.error);
    onValidityChange(seeded.ok);
  }, [value, onValidityChange]);

  function handleChange(next?: string) {
    const raw = next ?? "";
    setText(raw);
    const result = validate(raw);
    if (!result.ok) {
      setError(result.error);
      onValidityChange(false);
      return;
    }
    setError(null);
    onValidityChange(true);
    lastExternal.current = raw;
    onChange(result.value);
  }

  return (
    <div className={cn("flex flex-col gap-2", className)}>
      <div className="overflow-hidden rounded-md border">
        <Editor
          height={height}
          language="json"
          theme="vs-dark"
          value={text}
          onChange={handleChange}
          options={{
            minimap: { enabled: false },
            fontSize: 12,
            scrollBeyondLastLine: false,
            tabSize: 2,
            wordWrap: "on",
          }}
        />
      </div>
      {error && (
        <p className="inline-flex items-center gap-1.5 text-xs text-destructive">
          <AlertTriangle className="h-3.5 w-3.5" />
          {error} — fix it before approving.
        </p>
      )}
    </div>
  );
}

function stringify(value: Record<string, unknown>): string {
  return JSON.stringify(value ?? {}, null, 2);
}

type Validation =
  | { ok: true; value: Record<string, unknown> }
  | { ok: false; error: string };

/** The gate the submit button is wired to — see "the one hard rule" above. */
function validate(raw: string): Validation {
  if (!raw.trim()) {
    return { ok: false, error: "The proposal is empty." };
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch (parseError) {
    return {
      ok: false,
      error: parseError instanceof Error ? parseError.message : "Invalid JSON.",
    };
  }
  if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) {
    return { ok: false, error: "The proposal must be a JSON object." };
  }
  const value = parsed as Record<string, unknown>;
  if (Object.keys(value).length === 0) {
    return {
      ok: false,
      error:
        "The proposal has no fields — an empty object erases the entity's " +
        "enrichment rather than updating it, and nothing keeps the old one.",
    };
  }
  return { ok: true, value };
}
